/**
 * HybridPageChatPanel - Domain Component
 * 
 * A fixed overlay panel for the hybrid page that provides
 * either AI-powered search (Intel view) or inline advanced
 * filters (Filters view), toggled via a segmented control
 * in the mobile toolbar.
 * 
 * Mobile: Full-screen overlay with floating toolbar
 *   (back button, Filters/Intel toggle, listings count button).
 * Desktop: Floating card anchored to the bottom-right.
 * 
 * Design: Figma "Smartchat" — node 490:7382 (mobile), 494:8301 (desktop)
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { buildClassName } from 'utils/build-class-name';
import ChatBot from 'components/search-es/map-side-panel/ChatBot';
import InlinePanelFilters from 'components/domain/inline-panel-filters';
import FilterIcon from 'components/icon/filter-icon';
import SparkleIcon from 'components/icon/sparkle-icon';
import { FilterChipBar } from 'components/listing-filter-bar/filter_chip_bar';
import { AnalyticsController } from 'analytics/analytics-controller';
import {
  GTM_CLICK_HYBRID_PAGE_CHAT_CHIP,
  GTM_CLICK_HYBRID_PAGE_CHAT_FILTERS_TOGGLE,
} from 'constants/events';
import styles from './hybrid-page-chat-panel.module.scss';

import { CountryCodeList } from 'types/countries';

import type { Filter } from 'contexts/preferences/listing-params/types';
import type { FilterItemData, ListingFilterBarType } from 'components/listing-filter-bar/listing_filter_bar';
import type { PartialDeep } from 'type-fest';
import type { CountryCode } from 'types/countries';

/** The two views available inside the panel */
type PanelView = 'intel' | 'filters';

/** A suggestion chip shown below the welcome message / above the chat input. */
interface SuggestionChip {
  emoji: string;
  label: string;
}

/** Maximum number of suggestion chips visible at any one time. */
const MAX_VISIBLE_CHIPS = 4;

/**
 * Maps a site country to the example city used in the AI chat welcome
 * message. Fallback is "Miami" so US-focused tenants keep their current
 * copy if new countries are added without updating this map.
 */
const EXAMPLE_CITY_BY_COUNTRY: Record<CountryCode, string> = {
  [CountryCodeList.CANADA]: 'Toronto',
  [CountryCodeList.UNITED_STATES]: 'Miami',
};

/**
 * Pool of suggestion chips, ordered by priority.
 *
 * Chips are displayed from the top of the list, skipping any that the user
 * has already clicked. As clicked chips are removed, lower-priority chips
 * rotate up to fill their slots (always keeping at most MAX_VISIBLE_CHIPS
 * visible).
 */
const SUGGESTION_CHIPS: readonly SuggestionChip[] = [
  { emoji: '🛏️', label: '2+ Beds' },
  { emoji: '🛏️', label: '3+ Beds' },
  { emoji: '💰', label: '< $900K' },
  { emoji: '🏢', label: 'Condo' },
  { emoji: '🏠', label: 'House' },
  { emoji: '📐', label: '>1000 sqft' },
  { emoji: '📐', label: '>900 sqft' },
  { emoji: '🛏️', label: '4+ Beds' },
  { emoji: '🛁', label: '2+ Baths' },
  { emoji: '🏘️', label: 'Townhouse' },
  { emoji: '🚗', label: 'Garage' },
  { emoji: '🏊', label: 'Pool' },
  { emoji: '🔥', label: 'Fireplace' },
  { emoji: '🌊', label: 'Waterfront' },
  { emoji: '🏡', label: 'Open House' },
];

export interface HybridPageChatPanelProps {
  /** Whether the panel is open */
  isOpen: boolean;

  /** Callback to close the panel */
  onClose: () => void;

  /** Current search filters (passed through to ChatBot) */
  filters: ListingFilterBarType;

  /** Callback when filters change from chat interaction */
  onFiltersChanged?: (changedFilters: PartialDeep<Filter>, changedFilter?: string) => void;

  /** Callback when chat requests a map location change */
  onMapLocationChange?: (location: { lat: number; lng: number; zoom?: number }) => void;

  /** Total count of listings (displayed in chat and toolbar) */
  totalListingsCount?: number;

  /** Whether the user is on mobile */
  isMobile: boolean;

  /** Currently active filter items (for filter chips display) */
  activeFilterItems: FilterItemData[];

  /** Setter for active filter items (used when removing a chip) */
  setActiveFilterItems: (items: FilterItemData[]) => void;

  /** Country of the site, used to tailor examples in the chat welcome
   *  message (e.g. "Toronto" for Canada, "Miami" for the US).
   *  Defaults to Canada since this feature launched on the Canadian site. */
  country?: CountryCode;

  /** Additional className */
  className?: string;
}

/**
 * HybridPageChatPanel Component
 * 
 * Domain component that provides a fixed overlay panel with
 * two togglable views — Intel (AI chat) and Filters (inline
 * advanced filters) — for the hybrid page.
 */
export function HybridPageChatPanel({
  isOpen,
  onClose,
  filters,
  onFiltersChanged,
  onMapLocationChange,
  totalListingsCount,
  isMobile,
  activeFilterItems,
  setActiveFilterItems,
  country = CountryCodeList.CANADA,
  className,
}: HybridPageChatPanelProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [activeView, setActiveView] = useState<PanelView>('intel');
  // Chip labels the user has already clicked this session. Clicked chips are
  // hidden and replaced by the next chip in the pool, so the visible set
  // always shows fresh suggestions up to MAX_VISIBLE_CHIPS.
  const [usedChipLabels, setUsedChipLabels] = useState<Set<string>>(new Set());
  // Mirrors ChatBot's waiting-for-AI state. Used to hide the chip row while
  // a response is in flight so users aren't offered new suggestions until
  // the previous one has been processed.
  const [isChatLoading, setIsChatLoading] = useState(false);
  const clearChatRef = useRef<(() => void) | null>(null);
  const sendMessageRef = useRef<((text: string) => void) | null>(null);

  // Keep the component mounted during the CSS close transition.
  // `isAnimating` keeps the DOM present; `isOpen` drives the CSS class.
  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
    } else {
      const timer = setTimeout(() => {
        setIsAnimating(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Reset to Intel view when the panel is reopened
  useEffect(() => {
    if (isOpen) {
      setActiveView('intel');
    }
  }, [isOpen]);

  // Close handler used by mobile toolbar buttons
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Lock body scroll on mobile when panel is open
  useEffect(() => {
    if (isMobile && isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isMobile, isOpen]);

  // Handle suggestion chip click — appends the chip's label to the chat
  // input (comma-separated) and removes the chip from the visible pool.
  const handleChipClick = useCallback((label: string) => {
    AnalyticsController.trackEvent(GTM_CLICK_HYBRID_PAGE_CHAT_CHIP, 'All', label);
    sendMessageRef.current?.(label);
    setUsedChipLabels(prev => {
      if (prev.has(label)) return prev;
      const next = new Set(prev);
      next.add(label);
      return next;
    });
  }, []);

  // Switch to the Filters view (mobile segmented toggle).
  // Only fires when the user actually transitions from Intel → Filters,
  // so re-tapping the active toggle does not re-emit the event.
  const handleFiltersToggleClick = useCallback(() => {
    setActiveView(prev => {
      if (prev !== 'filters') {
        AnalyticsController.trackEvent(GTM_CLICK_HYBRID_PAGE_CHAT_FILTERS_TOGGLE);
      }
      return 'filters';
    });
  }, []);

  // Clear chat — forwards to ChatBot and also resets the used-chip set so
  // suggestions return to the full pool for the next conversation.
  const handleClearChat = useCallback(() => {
    clearChatRef.current?.();
    setUsedChipLabels(new Set());
  }, []);

  // Format listings count for the toolbar button
  const formattedCount = totalListingsCount != null
    ? totalListingsCount.toLocaleString()
    : '0';

  if (!isOpen && !isAnimating) {
    return null;
  }

  const visibleChips = isChatLoading
    ? []
    : SUGGESTION_CHIPS
      .filter(chip => !usedChipLabels.has(chip.label))
      .slice(0, MAX_VISIBLE_CHIPS);

  const suggestionChips = visibleChips.length > 0 ? (
    <div className={styles['suggestion-chips']}>
      {visibleChips.map(chip => (
        <button
          key={`${chip.emoji}-${chip.label}`}
          type="button"
          className={styles['chip']}
          onClick={() => handleChipClick(chip.label)}
        >
          <span className={styles['chip-emoji']}>{chip.emoji}</span>
          <span className={styles['chip-label']}>{chip.label}</span>
        </button>
      ))}
    </div>
  ) : null;

  const isIntelView = activeView === 'intel';
  const isFiltersView = activeView === 'filters';

  // Active filter chips bar — rendered on mobile in both Intel and Filters
  // views so users always have a quick way to see/remove active filters.
  // On desktop the chips live elsewhere in the hybrid page layout, so we
  // suppress them here to avoid duplication.
  const filterChipsBar = isMobile && activeFilterItems.length > 0 ? (
    <div className={styles['filter-chips-bar']}>
      <FilterChipBar
        activeFilterItems={activeFilterItems}
        setActiveFilterItems={setActiveFilterItems}
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onValueChange={onFiltersChanged ?? (() => {})}
        filters={filters}
        isSmall={true}
      />
    </div>
  ) : null;

  return (
    <>
      {/* Panel */}
      <div
        ref={panelRef}
        className={buildClassName(
          styles['panel'],
          isMobile ? styles['panel-mobile'] : styles['panel-desktop'],
          isOpen && styles['panel-open'],
          className
        )}
        role="dialog"
        aria-label={isIntelView ? 'Guided Search' : 'Advanced Filters'}
      >
        {/* ─── Mobile floating toolbar ─── */}
        {isMobile && (
          <div className={styles['mobile-toolbar']}>
            {/* Back button */}
            <button
              type="button"
              className={styles['back-button']}
              onClick={handleClose}
              aria-label="Close panel"
            >
              <svg width="10" height="16" viewBox="0 0 10 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M8.5 1L1.5 8L8.5 15" stroke="#0C0F24" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {/* Filters / Intel segmented toggle */}
            <div className={styles['toggle-bar']}>
              <button
                type="button"
                className={buildClassName(
                  styles['toggle-button'],
                  isFiltersView && styles['toggle-active']
                )}
                onClick={handleFiltersToggleClick}
                aria-current={isFiltersView ? 'true' : undefined}
              >
                <FilterIcon className={styles['toggle-icon']} />
                <span>Filters</span>
              </button>
              <button
                type="button"
                className={buildClassName(
                  styles['toggle-button'],
                  isIntelView && styles['toggle-active']
                )}
                onClick={() => setActiveView('intel')}
                aria-current={isIntelView ? 'true' : undefined}
              >
                <SparkleIcon className={styles['toggle-icon']} />
                <span>Guided</span>
              </button>
            </div>

            {/* Listings count button */}
            <button
              type="button"
              className={styles['listings-button']}
              onClick={handleClose}
            >
              <span>{formattedCount} Listings</span>
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5.25 3.5L8.75 7L5.25 10.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        )}

        {/* ─── Intel view (Chat) ─── */}
        {isIntelView && (
          <>
            {/* Header — Intel Search + BETA + Clear */}
            {filterChipsBar}
            <div className={styles['panel-header']}>
              <div className={styles['header-left']}>
                <h3 className={styles['panel-title']}>Guided Search</h3>
                <span className={styles['beta-badge']}>BETA</span>
              </div>
              <button
                type="button"
                className={styles['clear-button']}
                onClick={handleClearChat}
              >
                Clear
              </button>
            </div>

            {/* Chat area — ChatBot with hidden header */}
            <div className={styles['chat-area']}>
              <ChatBot
                onFiltersChanged={onFiltersChanged}
                onMapLocationChange={onMapLocationChange}
                filters={filters}
                totalListingsCount={totalListingsCount}
                disableLocationSearch
                hideHeader
                clearRef={clearChatRef}
                sendMessageRef={sendMessageRef}
                renderBeforeInput={suggestionChips}
                onlyShowListingCountResponse={true}
                onLoadingChange={setIsChatLoading}
                exampleCity={EXAMPLE_CITY_BY_COUNTRY[country] ?? EXAMPLE_CITY_BY_COUNTRY[CountryCodeList.UNITED_STATES]}
              />
            </div>
          </>
        )}

        {/* ─── Filters view (inline advanced filters) ─── */}
        {isFiltersView && (
          <>
            {filterChipsBar}
            <InlinePanelFilters
              filters={filters}
              onApply={(appliedFilters, changedFilter) => {
                onFiltersChanged?.(appliedFilters, changedFilter);
              }}
            />
          </>
        )}
      </div>
    </>
  );
}

export default HybridPageChatPanel;
